import { WAVEFORM_SAMPLES } from '@/protocol/limits';

/**
 * The 100-sample waveform Section 14 shows beside a voice message.
 *
 * The authoritative samples come from the media worker on the server and arrive
 * on `MediaVariant.waveform`; that is what a *received* voice message renders.
 * This module exists for the other case: a message the user has just recorded
 * and not yet sent. Its media does not exist on the server, so there is nothing
 * to fetch, and showing a flat bar for the duration of the review step would
 * make the review useless — the user is trying to decide whether the recording
 * captured anything at all.
 *
 * The computation is a root-mean-square per bucket, normalized to the loudest
 * bucket, which is the same shape a peak-per-bucket renderer produces but is
 * less dominated by single-sample clicks.
 */

/**
 * `AudioContext` is only in the DOM lib under its window name, and
 * `webkitAudioContext` is still how Safari exposes it.
 */
interface AudioContextConstructorLike {
  new (options?: { sampleRate?: number }): AudioContextLike;
}

interface AudioContextLike {
  decodeAudioData: (data: ArrayBuffer) => Promise<AudioBufferLike>;
  close: () => Promise<void>;
}

interface AudioBufferLike {
  readonly duration: number;
  readonly numberOfChannels: number;
  getChannelData: (channel: number) => Float32Array;
}

function audioContextConstructor(): AudioContextConstructorLike | undefined {
  const scope = globalThis as {
    AudioContext?: AudioContextConstructorLike;
    webkitAudioContext?: AudioContextConstructorLike;
  };
  return scope.AudioContext ?? scope.webkitAudioContext;
}

export interface LocalWaveform {
  samples: number[];
  durationMs: number;
}

/**
 * Computes a local preview waveform, or returns null when the browser cannot
 * decode the recording.
 *
 * A null result is not an error the user needs to see: the recording is still
 * valid and still sendable, and the server will produce the real waveform. The
 * player falls back to a duration-only presentation.
 */
export async function computeWaveform(blob: Blob): Promise<LocalWaveform | null> {
  const Constructor = audioContextConstructor();
  if (!Constructor) {
    return null;
  }
  let context: AudioContextLike | null = null;
  try {
    context = new Constructor();
    const buffer = await context.decodeAudioData(await blob.arrayBuffer());
    // Section 06 captures voice in mono, so the first channel is the whole
    // signal; averaging channels would only matter for imported stereo audio.
    const channel = buffer.getChannelData(0);
    const bucketSize = Math.max(1, Math.floor(channel.length / WAVEFORM_SAMPLES));

    const buckets: number[] = [];
    let peak = 0;
    for (let index = 0; index < WAVEFORM_SAMPLES; index += 1) {
      const start = index * bucketSize;
      const end = Math.min(channel.length, start + bucketSize);
      let sumOfSquares = 0;
      for (let position = start; position < end; position += 1) {
        const sample = channel[position] ?? 0;
        sumOfSquares += sample * sample;
      }
      const rms = end > start ? Math.sqrt(sumOfSquares / (end - start)) : 0;
      peak = Math.max(peak, rms);
      buckets.push(rms);
    }

    // Normalized to 0–100 so the stored shape matches the server's `uint32[]`
    // and a renderer does not have to know which source produced it.
    const scale = peak > 0 ? 100 / peak : 0;
    return {
      samples: buckets.map((value) => Math.round(value * scale)),
      durationMs: Math.round(buffer.duration * 1000),
    };
  } catch {
    return null;
  } finally {
    void context?.close().catch(() => {
      // Closing a context that never opened throws in some engines; there is
      // nothing to recover and nothing to report.
    });
  }
}

/** Pads or truncates a stored waveform to the fixed sample count. */
export function normalizeWaveform(samples: readonly number[]): number[] {
  if (samples.length === WAVEFORM_SAMPLES) {
    return [...samples];
  }
  if (samples.length === 0) {
    return new Array<number>(WAVEFORM_SAMPLES).fill(0);
  }
  const out: number[] = [];
  for (let index = 0; index < WAVEFORM_SAMPLES; index += 1) {
    const source = Math.floor((index * samples.length) / WAVEFORM_SAMPLES);
    out.push(samples[source] ?? 0);
  }
  return out;
}
